import React, { useEffect, useMemo, useState } from "react";

import { DATE_TIME_CONFIG } from "../../../config";
import {
  formatMonthYear,
  getDateInLocalTZFromDateStr,
  getDateString,
  getDaysFromStartOfWeek,
  today,
  toLocalCalendarDay,
} from "../../../utils/datetime";
import { ChevronLeft, ChevronRight } from "../../icons/svg/outline";

const WEEKDAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

/**
 * Build ordered weekday labels for a week-start offset.
 *
 * @param {number} startOfWeek - 0 = Sunday … 6 = Saturday.
 * @returns {string[]}
 */
function getWeekdayLabels(startOfWeek) {
  return [
    ...WEEKDAY_LABELS.slice(startOfWeek),
    ...WEEKDAY_LABELS.slice(0, startOfWeek),
  ];
}

/**
 * Build a week-aligned grid of local date keys (or null) for a calendar month.
 * Only pads trailing cells to finish the last week — no empty extra row.
 *
 * @param {number} year - Full year.
 * @param {number} monthIndex - 0-based month.
 * @param {number} startOfWeek - Week start day index.
 * @returns {Array<string|null>}
 */
function buildMonthCells(year, monthIndex, startOfWeek) {
  const firstOfMonth = new Date(year, monthIndex, 1);
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  const leading = getDaysFromStartOfWeek(startOfWeek, firstOfMonth.getDay());
  const cells = [];

  for (let i = 0; i < leading; i += 1) {
    cells.push(null);
  }
  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push(getDateString(new Date(year, monthIndex, day)));
  }
  // Finish the last week only (avoid a blank 6th row when unused).
  while (cells.length % 7 !== 0) {
    cells.push(null);
  }
  return cells;
}

/**
 * Resolve the month to display from a selected value or today.
 *
 * @param {string} value - ``YYYY-MM-DD`` or empty.
 * @returns {{year: number, monthIndex: number}}
 */
function getViewMonthFromValue(value) {
  const base = value
    ? getDateInLocalTZFromDateStr(value)
    : toLocalCalendarDay(today());
  return {
    year: base.getFullYear(),
    monthIndex: base.getMonth(),
  };
}

/**
 * Inline month calendar for picking a single local date (no extra click to open).
 *
 * @param {Object} props
 * @param {string} [props.value=""] - Selected ``YYYY-MM-DD``.
 * @param {(next: string) => void} props.onChange - Fired when a day is chosen.
 * @param {string} [props.min] - Inclusive min ``YYYY-MM-DD``.
 * @param {string} [props.max] - Inclusive max ``YYYY-MM-DD``.
 * @param {string} [props.className=""] - Optional wrapper class.
 * @returns {JSX.Element}
 */
const InlineDatePicker = ({
  value = "",
  onChange,
  min,
  max,
  className = "",
}) => {
  const startOfWeek = DATE_TIME_CONFIG.START_OF_WEEK.default;
  const weekdayLabels = useMemo(
    () => getWeekdayLabels(startOfWeek),
    [startOfWeek]
  );
  const [viewMonth, setViewMonth] = useState(() => getViewMonthFromValue(value));

  useEffect(() => {
    setViewMonth(getViewMonthFromValue(value));
  }, [value]);

  const cells = useMemo(
    () => buildMonthCells(viewMonth.year, viewMonth.monthIndex, startOfWeek),
    [viewMonth.year, viewMonth.monthIndex, startOfWeek]
  );

  const viewDate = new Date(viewMonth.year, viewMonth.monthIndex, 1);
  const todayKey = getDateString(toLocalCalendarDay(today()));

  /**
   * Move the visible month by ``delta`` months.
   *
   * @param {number} delta
   * @returns {void}
   */
  const shiftMonth = (delta) => {
    setViewMonth((current) => {
      const next = new Date(current.year, current.monthIndex + delta, 1);
      return {
        year: next.getFullYear(),
        monthIndex: next.getMonth(),
      };
    });
  };

  /**
   * Whether a date key is outside min/max bounds.
   *
   * @param {string} dateKey
   * @returns {boolean}
   */
  const isDisabled = (dateKey) => {
    if (min && dateKey < min) return true;
    if (max && dateKey > max) return true;
    return false;
  };

  return (
    <div className={className}>
      <div className="mb-2 font-inter flex items-center justify-between gap-2">
        <button
          type="button"
          aria-label="Previous month"
          onClick={() => shiftMonth(-1)}
          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-500 transition hover:bg-gray-100 hover:text-gray-800"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden />
        </button>
        <div className="text-sm font-semibold text-gray-900">
          {formatMonthYear(viewDate)}
        </div>
        <button
          type="button"
          aria-label="Next month"
          onClick={() => shiftMonth(1)}
          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-500 transition hover:bg-gray-100 hover:text-gray-800"
        >
          <ChevronRight className="h-4 w-4" aria-hidden />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-0.5 text-center text-[11px] font-medium text-gray-400">
        {weekdayLabels.map((label) => (
          <div key={label} className="py-1">
            {label}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-0.5">
        {cells.map((dateKey, index) => {
          if (!dateKey) {
            return <div key={`empty-${index}`} className="h-8" />;
          }

          const selected = dateKey === value;
          const isToday = dateKey === todayKey;
          const disabled = isDisabled(dateKey);

          return (
            <button
              key={dateKey}
              type="button"
              disabled={disabled}
              aria-label={dateKey}
              aria-pressed={selected}
              onClick={() => onChange(dateKey)}
              className={[
                "h-8 rounded-md text-sm transition",
                disabled
                  ? "cursor-not-allowed text-gray-300"
                  : "text-gray-800 hover:bg-secondary-50",
                selected
                  ? "bg-secondary-500 font-semibold text-white hover:bg-secondary-500"
                  : "",
                !selected && isToday ? "font-semibold text-secondary-700" : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              {Number(dateKey.slice(8, 10))}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default InlineDatePicker;
